/**
 * The Windmill mark and lockup.
 *
 * The mark is inline SVG traced from the supplied artwork — an accordion-folded
 * W in the light face and the amber fold face, with vertical folds at x = 149,
 * 270 and 391 of its 540x354 box. Inline rather than an <img> so the lockup's
 * type uses the app's own font and both scale crisply at any size.
 */

export function Mark({ className = '', title }: { className?: string; title?: string }) {
  return (
    <svg
      viewBox="0 0 540 354"
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <polygon points="0,26.9 58.9,26.9 149,102.7 149,354 0,205.7" fill="#F4C215" />
      <polygon points="149,102.7 270,0 270,244.7 149,354" fill="#FAA41B" />
      <polygon points="270,0 391,102.7 391,354 270,244.7" fill="#F4C215" />
      <polygon points="391,102.7 481.1,26.9 540,26.9 540,205.7 391,354" fill="#FAA41B" />
    </svg>
  )
}

/** The mark on its rounded ink plate, as it appears as an app icon. */
export function MarkPlate({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-lg bg-ink ${className}`}
    >
      <Mark className="w-[62%]" />
    </span>
  )
}

/**
 * The stacked lockup: mark, then the name in the ink bar with its brand rule,
 * then the descriptor. Used on the sign-in screen.
 */
export function Lockup({ orgName }: { orgName: string }) {
  // Only the real company name gets the full lockup treatment; a renamed org
  // falls back to plain type so the bar never contains something it shouldn't.
  const isWindmill = /^windmill\b/i.test(orgName.trim())
  const [first, ...rest] = orgName.trim().split(/\s+/)

  return (
    <div className="flex flex-col items-center gap-4">
      <Mark className="h-16 w-auto" title={`${orgName} logo`} />
      {isWindmill ? (
        <div className="flex flex-col items-center gap-1.5">
          <span className="border-2 border-brand-deep bg-ink px-5 py-1.5">
            <span className="block text-2xl leading-none font-extrabold tracking-[0.06em] text-on-ink uppercase">
              {first}
            </span>
          </span>
          {rest.length > 0 && (
            <span className="font-mono text-[0.6875rem] font-semibold tracking-[0.32em] text-brand-deep uppercase">
              {rest.join(' ')}
            </span>
          )}
        </div>
      ) : (
        <h1 className="text-center text-2xl">{orgName}</h1>
      )}
    </div>
  )
}
